// import moment from "moment";
import { thor } from "../../typings/thor";
import { Enums, Events } from "../../thor.enums";
import { isAdvertiser, isQuotaConsumed } from "../../utils/organization";
import { isSaleCreated } from "../../utils/sale";
import { ItemModificationCounts, OrderCounts } from "../../typings/sale";

export class SummaryController {
    public static $inject: string[] = [
        "$mdDialog",
        "$mdMedia",
        "$rootScope",
        "$timeout",
        "$translate",
        "absenteeBidsService",
        "authService",
        "categoryService",
        "quotasService",
        "saleService",
        "toaster",
        "sitesService",
        "configService",
        "utilsService"
    ];

    public isFirstPublication: boolean;
    public isSaleEdited: boolean;
    public isSaving: boolean;
    public itemModificationCounters: ItemModificationCounts;
    public ordersCounters: OrderCounts;
    public secretsOrdersCounters: OrderCounts;
    public remindPaymentProviderChanged: boolean = false;
    public saleDateAfterStripeEnd: boolean = false;
    public supportPhone: string;
    public stripeEnd: Date;
    public formattedStripeEnd: string;

    private form: ng.IFormController;
    private publishing: boolean;
    private sale: any; // bind from parent (SaleEditController::initiatePublish)

    private $t;

    constructor(
        private $mdDialog: ng.material.IDialogService,
        public $mdMedia: ng.material.IMedia,
        private $rootScope: ng.IRootScopeService,
        $timeout: ng.ITimeoutService,
        $translate: ng.translate.ITranslateService,
        private absenteeBidsService,
        private authService,
        public categoryService,
        private quotasService,
        public saleService,
        private toaster,
        public sitesService,
        configService,
        utils
    ) {
        this.$t = $translate.instant;

        const { organization } = authService;
        const locale = utils.getCurrentLang();
        this.stripeEnd = new Date(
            new Date(configService.stripeSaleCreationDeadline).getUTCFullYear(),
            new Date(configService.stripeSaleCreationDeadline).getUTCMonth(),
            new Date(configService.stripeSaleCreationDeadline).getUTCDate()
        );

        this.formattedStripeEnd = new Date(
            new Date(configService.stripeSaleCreationDeadline).getUTCFullYear(),
            new Date(configService.stripeSaleCreationDeadline).getUTCMonth(),
            new Date(configService.stripeSaleCreationDeadline).getUTCDate() -1
        ).toLocaleString(locale,  { day: "2-digit", month: "2-digit" });

        if (!isSaleCreated(this.sale.resource) || !isAdvertiser(organization)) {
            return;
        }

        quotasService
            .get(organization.id)
            .then(
                _onFulfilled => false,
                _onRejected => true
            )
            .then((hasQuotaError: boolean) =>
                $timeout(() => {
                    const yearQuota = this.quotasService.getYearQuota(this.sale.resource);
                    const quotasValid = !hasQuotaError && !isQuotaConsumed(yearQuota);
                    this.form?.$setValidity("quotaFullyConsumed", quotasValid, null);
                })
            );
    }

    public $onInit() {
        this._setUp();
    }

    public cancel() {
        this.$mdDialog.cancel();
    }

    public isCounterModified() {
        for (const counterName in this.itemModificationCounters) {
            if (this.itemModificationCounters[counterName] > 0) {
                return true;
            }
        }
        return false;
    }

    public preventSubmit() {
        return this.form.$invalid || this.publishing;
    }

    public publishSale() {
        this.publishing = true;

        this.sale
            .publishSale()
            .then(() => {
                this.$rootScope.$broadcast(Events.PostMessageToV2);
                this.toaster.show(this.$t("publication_effectuee_avec_succes"));
            })
            .catch(error =>
                (error?.data?.message || "").includes("reached")
                    ? this.toaster.toastQuotasContactEmailBusiness(true)
                    : this.toaster.show(this.$t("la_publication_a_echoue"), true)
            )
            .finally(() => this.$mdDialog.hide());
    }

    private setCounters() {
        this.itemModificationCounters = {
            newItems: 0,
            modifiedItems: 0,
            highlightItems: 0,
            hiddenItems: 0,
            suppressedItems: 0,
            publishedItems: 0,
            auctioned: 0,
            percentAuctioned: 0
        };
        this.ordersCounters = {
            modified: 0,
            suppressed: 0
        };
        this.secretsOrdersCounters = {
            modified: 0,
            suppressed: 0
        };
    }

    private _setUp() {
        this.publishing = false;
        this.isFirstPublication = this.sale.resource.status === Enums.SaleStatus.Created;
        this.isSaleEdited = this.sale.resource.status === Enums.SaleStatus.Edited;
        this.supportPhone = this.authService.isInternational()
        ? this.sitesService.contactPhoneInter
        : this.sitesService.contactPhone;
        this.saleService
            .getPublished(this.sale.resource.id)
            .then((publishedSale) => {
                if (this.sale.resource.sites.interencheres.type === "live") {
                    if (
                        new Date(publishedSale.datetime) < new Date(this.stripeEnd) &&
                        new Date(this.sale.resource.datetime) >= new Date(this.stripeEnd)
                    ) {
                        this.saleDateAfterStripeEnd = true;
                        this.remindPaymentProviderChanged = true;
                    }
                    if (
                        new Date(publishedSale.datetime) > new Date(this.stripeEnd) &&
                        new Date(this.sale.resource.datetime) <= new Date(this.stripeEnd)
                    ) {
                        this.remindPaymentProviderChanged = true;
                    }
                }
        });

        this.setCounters();

        this.sale.items.forEach((item: thor.ItemGet) => {
            const itemOrder = this.absenteeBidsService.getAbsenteeBidsCountForItem(item.id);
            if (item.states.suppressed || item.states.liveHidden) {
                if (item.status !== "created") {
                    this.itemModificationCounters.suppressedItems++;
                    if (item.secretOrderCount) {
                        this.secretsOrdersCounters.suppressed += item.secretOrderCount;
                    }
                    if (itemOrder) {
                        this.ordersCounters.suppressed++;
                    }
                }
            } else {
                if (item.highlight && !item.states.hidden) {
                    this.itemModificationCounters.highlightItems++;
                }
                if (item.status === Enums.ItemStatus.Created) {
                    this.itemModificationCounters.newItems++;
                }
                if (item.status === Enums.ItemStatus.Modified) {
                    this.itemModificationCounters.modifiedItems++;
                    if (item.secretOrderCount) {
                        this.secretsOrdersCounters.modified += item.secretOrderCount;
                    }
                    if (itemOrder) {
                        this.ordersCounters.modified++;
                    }
                }
                if (item.states.hidden) {
                    this.itemModificationCounters.hiddenItems++;
                } else {
                    this.itemModificationCounters.publishedItems++;
                }
                if (this.sale.resource.isPast && item.pricing && item.pricing.auctioned) {
                    this.itemModificationCounters.auctioned++;
                }
            }
        });

        this.itemModificationCounters.percentAuctioned =
            (this.itemModificationCounters.auctioned * 100) / this.sale.items.length;
    }
}
